import type { EventSubtask } from "../../../shared/types";
import { useDailyChecks } from "../hooks/use-daily-checks";

interface DailyChecklistProps {
  readonly eventTitle: string;
  readonly subtasks: readonly EventSubtask[];
  readonly readOnly?: boolean;
}

export function DailyChecklist({ eventTitle, subtasks, readOnly }: DailyChecklistProps) {
  const { isChecked, toggleCheck, getCheckedCount, resetChecks } =
    useDailyChecks();

  const sorted = [...subtasks].sort((a, b) => a.priority - b.priority);
  const checkedCount = getCheckedCount(eventTitle, subtasks);
  const allDone = checkedCount === subtasks.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-ink-muted">
          Checklist ({checkedCount}/{subtasks.length})
        </span>
        {checkedCount > 0 && !readOnly && (
          <button
            type="button"
            onClick={() => resetChecks(eventTitle)}
            className="text-xs text-ink-faint hover:text-ink focus:outline-none"
          >
            Tout décocher
          </button>
        )}
      </div>

      {allDone && (
        <div className="mb-2 px-2 py-1 bg-surface border border-border text-xs text-ink-muted text-center">
          Créneau libéré ✦ Pause
        </div>
      )}

      <ul className="space-y-1">
        {sorted.map((subtask) => {
          const checked = isChecked(eventTitle, subtask.id);
          return (
            <li key={subtask.id} className="flex items-center gap-2">
              <label className={`flex items-center shrink-0 ${readOnly ? "cursor-default" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleCheck(eventTitle, subtask.id);
                  }}
                  className="sr-only"
                />
                <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center
                  ${checked
                    ? "bg-accent border-accent"
                    : "border-border hover:border-accent/50"
                  }`}>
                  {checked && (
                    <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </label>
              <span
                className={`text-sm select-none ${checked ? "line-through opacity-40" : "text-ink"}`}
              >
                {subtask.title}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
